// LoginPage — form de login com validação Zod inline + RHF.
// Lê redirect alvo de location.state.from (vindo de RequireAuth).
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Toast, useToast } from '@/components/ui/Toast';
import { ArrowRightIcon, CheckIcon, EyeIcon, EyeOffIcon } from '@/components/icons';
import { landingRouteForRole, ROUTES } from '@/app/router/paths';
import { useLoginMutation } from '../api';
import { GoogleButton } from '../components/GoogleButton';
import { useGoogleAuth } from '../useGoogleAuth';
import { parseAuthError } from '../errors';
import { LogoMark } from './shared/LogoMark';
import styles from './LoginPage.module.css';

const schema = z.object({
  email: z.string().email('Informe um e-mail válido.'),
  password: z.string().min(6, 'A senha precisa ter no mínimo 6 caracteres.'),
  remember: z.boolean().optional(),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPwd, setShowPwd] = useState(false);
  const [comingSoon, setComingSoon] = useState<string | null>(null);
  const [login, { isLoading, error }] = useLoginMutation();
  const google = useGoogleAuth();
  const toast = useToast();

  useEffect(() => {
    if (error) toast.danger({ title: 'Não foi possível entrar', description: parseAuthError(error) });
  }, [error, toast]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const session = await login({ email: values.email, password: values.password }).unwrap();
      // Sem rota de origem, cada papel tem a sua casa (RF-01): o ATENDENTE cai no
      // guichê, os demais no painel.
      const target =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ??
        landingRouteForRole(session.user.role);
      navigate(target, { replace: true });
    } catch {
      // erro vira mensagem via `error` do hook
    }
  });

  return (
    <main aria-labelledby="login-title">
      <LogoMark />
      <div className={styles.eyebrow}>Acesso da equipe</div>
      <h1 id="login-title" className={styles.title}>
        Bem-vindo de volta
      </h1>
      <p className={styles.subtitle}>
        Entre para continuar atendendo com sua equipe — e com a IA trabalhando ao seu lado.
      </p>

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          placeholder="voce@empresa.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <div className={styles.passwordField}>
          <Input
            label="Senha"
            type={showPwd ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            error={errors.password?.message}
            {...register('password')}
          />
          <button
            type="button"
            className={styles.eyeToggle}
            onClick={() => setShowPwd((v) => !v)}
            aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={showPwd}
          >
            {showPwd ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
          </button>
        </div>

        <div className={styles.row}>
          <label className={styles.remember}>
            <input
              type="checkbox"
              className={styles.checkInput}
              {...register('remember')}
              defaultChecked
            />
            <span className={styles.checkBox} aria-hidden="true">
              <CheckIcon size={12} strokeWidth={3} />
            </span>
            Manter conectado
          </label>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setComingSoon('Em breve: recuperação de senha por e-mail.')}
          >
            Esqueci a senha
          </button>
        </div>

        <Button
          type="submit"
          size="lg"
          loading={isLoading}
          trailingIcon={<ArrowRightIcon size={18} />}
        >
          Entrar
        </Button>
      </form>

      <div className={styles.divider}>
        <span>ou</span>
      </div>

      <GoogleButton onCredential={google.onCredential} className={styles.fullWidth} />

      <p className={styles.footer}>
        Ainda não tem conta?{' '}
        <Link to={ROUTES.signup} className={styles.link}>
          Criar conta
        </Link>
      </p>

      {comingSoon && (
        <div className={styles.toastSlot}>
          <Toast
            tone="info"
            title="Em breve"
            description={comingSoon}
            onDismiss={() => setComingSoon(null)}
          />
        </div>
      )}
    </main>
  );
}
